/**
 * API Client for backend communication
 */
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

class APIClient {
  constructor() {
    this.baseURL = API_BASE_URL;
    this.token = null;
  }

  setToken(token) {
    this.token = token || null;
  }

  getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
    };
    if (this.token) {
      headers['Authorization'] = `Bearer ${this.token}`;
    }
    return headers;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    const config = {
      ...options,
      headers: {
        ...this.getHeaders(),
        ...options.headers,
      },
    };

    try {
      const response = await fetch(url, config);
      
      if (!response.ok) {
        const error = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(error.detail || `HTTP error! status: ${response.status}`);
      }

      // Handle 204 No Content
      if (response.status === 204) {
        return null;
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Profile (auth token set externally by AuthContext)
  async getCurrentUser() {
    return this.request('/api/auth/me');
  }

  // Forum - Categories
  async getCategories() {
    return this.request('/api/forum/categories');
  }

  async getCategory(categoryId) {
    return this.request(`/api/forum/categories/${categoryId}`);
  }

  // Forum - Posts
  async getPosts(params = {}) {
    const queryParams = new URLSearchParams();
    if (params.category_id) queryParams.append('category_id', params.category_id);
    if (params.search) queryParams.append('search', params.search);
    if (params.page) queryParams.append('page', params.page);
    if (params.limit) queryParams.append('limit', params.limit);
    
    const queryString = queryParams.toString();
    return this.request(`/api/forum/posts${queryString ? `?${queryString}` : ''}`);
  }

  async getPost(postId) {
    return this.request(`/api/forum/posts/${postId}`);
  }

  async createPost(title, content, categoryId) {
    return this.request('/api/forum/posts', {
      method: 'POST',
      body: JSON.stringify({ title, content, category_id: categoryId }),
    });
  }

  async updatePost(postId, data) {
    return this.request(`/api/forum/posts/${postId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async deletePost(postId) {
    return this.request(`/api/forum/posts/${postId}`, {
      method: 'DELETE',
    });
  }

  async getMyPosts(params = {}) {
    const qp = new URLSearchParams();
    if (params.page) qp.append('page', params.page);
    if (params.limit) qp.append('limit', params.limit);
    const qs = qp.toString();
    return this.request(`/api/forum/posts/me${qs ? `?${qs}` : ''}`);
  }

  async hidePost(postId) {
    return this.request(`/api/forum/posts/${postId}/hide`, { method: 'POST' });
  }

  async unhidePost(postId) {
    return this.request(`/api/forum/posts/${postId}/unhide`, { method: 'POST' });
  }

  // Forum - Replies
  async getReplies(postId) {
    return this.request(`/api/forum/posts/${postId}/replies`);
  }

  async createReply(postId, content, parentReplyId = null) {
    return this.request(`/api/forum/posts/${postId}/replies`, {
      method: 'POST',
      body: JSON.stringify({ content, parent_reply_id: parentReplyId }),
    });
  }

  // Forum - Search
  async searchPosts(query, categoryId = null) {
    const queryParams = new URLSearchParams({ q: query });
    if (categoryId) queryParams.append('category_id', categoryId);
    return this.request(`/api/forum/search?${queryParams.toString()}`);
  }

  // AI Agent
  async chat(message, conversationId = null) {
    return this.request('/api/ai/chat', {
      method: 'POST',
      body: JSON.stringify({ message, conversation_id: conversationId }),
    });
  }

  async semanticSearch(query, categoryId = null, limit = 10) {
    return this.request('/api/ai/search', {
      method: 'POST',
      body: JSON.stringify({ query, category_id: categoryId, limit }),
    });
  }

  async summarizePost(postId, includeReplies = true) {
    return this.request('/api/ai/summarize', {
      method: 'POST',
      body: JSON.stringify({ post_id: postId, include_replies: includeReplies }),
    });
  }

  // ── HRIS ──────────────────────────────────────────────

  // Departments
  async getDepartments(activeOnly = true, tree = false) {
    const qp = new URLSearchParams();
    qp.append('active_only', activeOnly);
    if (tree) qp.append('tree', 'true');
    return this.request(`/api/v1/hr/departments?${qp.toString()}`);
  }
  async getDepartment(id) {
    return this.request(`/api/v1/hr/departments/${id}`);
  }
  async createDepartment(data) {
    return this.request('/api/v1/hr/departments', { method: 'POST', body: JSON.stringify(data) });
  }
  async updateDepartment(id, data) {
    return this.request(`/api/v1/hr/departments/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  // Positions
  async getPositions(activeOnly = true) {
    return this.request(`/api/v1/hr/positions?active_only=${activeOnly}`);
  }
  async createPosition(data) {
    return this.request('/api/v1/hr/positions', { method: 'POST', body: JSON.stringify(data) });
  }
  async updatePosition(id, data) {
    return this.request(`/api/v1/hr/positions/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  // People
  async getPeople(params = {}) {
    const qp = new URLSearchParams();
    if (params.search) qp.append('search', params.search);
    if (params.department_id) qp.append('department_id', params.department_id);
    if (params.position_id) qp.append('position_id', params.position_id);
    if (params.status) qp.append('status', params.status);
    if (params.employment_type) qp.append('employment_type', params.employment_type);
    if (params.sort) qp.append('sort', params.sort);
    if (params.page) qp.append('page', params.page);
    if (params.limit) qp.append('limit', params.limit);
    const qs = qp.toString();
    return this.request(`/api/v1/hr/people${qs ? `?${qs}` : ''}`);
  }
  async getPerson(id) {
    return this.request(`/api/v1/hr/people/${id}`);
  }
  async createPerson(data) {
    return this.request('/api/v1/hr/people', { method: 'POST', body: JSON.stringify(data) });
  }
  async updatePerson(id, data) {
    return this.request(`/api/v1/hr/people/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async deletePerson(id) {
    return this.request(`/api/v1/hr/people/${id}`, { method: 'DELETE' });
  }
  async bulkUpdateStatus(employeeIds, status) {
    return this.request('/api/v1/hr/people/bulk-status', {
      method: 'POST',
      body: JSON.stringify({ employee_ids: employeeIds, status }),
    });
  }

  // Audit
  async getAuditLog(params = {}) {
    const qp = new URLSearchParams();
    if (params.employee_id) qp.append('employee_id', params.employee_id);
    if (params.page) qp.append('page', params.page);
    if (params.limit) qp.append('limit', params.limit);
    const qs = qp.toString();
    return this.request(`/api/v1/hr/audit${qs ? `?${qs}` : ''}`);
  }

  // Onboarding Tasks
  async getOnboardingTasks(empId) {
    return this.request(`/api/v1/hr/people/${empId}/onboarding-tasks`);
  }
  async createOnboardingTask(empId, data) {
    return this.request(`/api/v1/hr/people/${empId}/onboarding-tasks`, { method: 'POST', body: JSON.stringify(data) });
  }
  async updateOnboardingTask(taskId, data) {
    return this.request(`/api/v1/hr/onboarding-tasks/${taskId}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  // Leave Requests
  async getLeaveRequests(empId) {
    return this.request(`/api/v1/hr/people/${empId}/leave`);
  }
  async createLeaveRequest(empId, data) {
    return this.request(`/api/v1/hr/people/${empId}/leave`, { method: 'POST', body: JSON.stringify(data) });
  }
  async approveLeave(requestId) {
    return this.request(`/api/v1/hr/leave/${requestId}/approve?approved=true`, { method: 'POST' });
  }
  async denyLeave(requestId) {
    return this.request(`/api/v1/hr/leave/${requestId}/deny`, { method: 'POST' });
  }

  // Benefit Plans
  async getBenefitPlans(activeOnly = true) {
    return this.request(`/api/v1/hr/benefit-plans?active_only=${activeOnly}`);
  }
  async createBenefitPlan(data) {
    return this.request('/api/v1/hr/benefit-plans', { method: 'POST', body: JSON.stringify(data) });
  }
  async updateBenefitPlan(id, data) {
    return this.request(`/api/v1/hr/benefit-plans/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async getBenefitsForPerson(empId) {
    return this.request(`/api/v1/hr/people/${empId}/benefits`);
  }
  async enrollBenefit(empId, data) {
    return this.request(`/api/v1/hr/people/${empId}/benefits`, { method: 'POST', body: JSON.stringify(data) });
  }

  // 1:1 Notes
  async getOneOnOneNotes(empId) {
    return this.request(`/api/v1/hr/people/${empId}/one-on-one-notes`);
  }
  async createOneOnOneNote(empId, data) {
    return this.request(`/api/v1/hr/people/${empId}/one-on-one-notes`, { method: 'POST', body: JSON.stringify(data) });
  }

  // Analytics
  async getAnalytics() {
    return this.request('/api/v1/hr/analytics');
  }

  // Review Cycles
  async getReviewCycles(status) {
    const qs = status ? `?status=${status}` : '';
    return this.request(`/api/v1/hr/review-cycles${qs}`);
  }
  async createReviewCycle(data) {
    return this.request('/api/v1/hr/review-cycles', { method: 'POST', body: JSON.stringify(data) });
  }
  async updateReviewCycle(id, data) {
    return this.request(`/api/v1/hr/review-cycles/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }

  // Performance Reviews
  async getReviewsForEmployee(empId) {
    return this.request(`/api/v1/hr/people/${empId}/reviews`);
  }
  async getReviewsForCycle(cycleId) {
    return this.request(`/api/v1/hr/review-cycles/${cycleId}/reviews`);
  }
  async createPerformanceReview(data) {
    return this.request('/api/v1/hr/reviews', { method: 'POST', body: JSON.stringify(data) });
  }
  async updatePerformanceReview(id, data) {
    return this.request(`/api/v1/hr/reviews/${id}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  // Self-Service Profile (requires auth token)
  async getMyProfile() {
    return this.request('/api/v1/me/profile');
  }
  async updateMyProfile(data) {
    return this.request('/api/v1/me/profile', { method: 'PUT', body: JSON.stringify(data) });
  }
  async getMyTimeOff() {
    return this.request('/api/v1/me/time-off');
  }
  async createMyLeaveRequest(data) {
    return this.request('/api/v1/me/leave', { method: 'POST', body: JSON.stringify(data) });
  }
  async getMyBenefits() {
    return this.request('/api/v1/me/benefits');
  }
  async getMyOnboardingTasks() {
    return this.request('/api/v1/me/onboarding-tasks');
  }
  async updateMyOnboardingTask(taskId, data) {
    return this.request(`/api/v1/me/onboarding-tasks/${taskId}`, { method: 'PATCH', body: JSON.stringify(data) });
  }
  async getMyTeam() {
    return this.request('/api/v1/me/team');
  }
  async getMyNotifications(unreadOnly = false) {
    return this.request(`/api/v1/me/notifications?unread_only=${unreadOnly}`);
  }
  async markNotificationRead(notificationId) {
    return this.request(`/api/v1/me/notifications/${notificationId}/read`, { method: 'PATCH' });
  }
  async getUpcomingLeave(params = {}) {
    const qp = new URLSearchParams();
    if (params.start_date) qp.append('start_date', params.start_date);
    if (params.end_date) qp.append('end_date', params.end_date);
    if (params.department_id) qp.append('department_id', params.department_id);
    const qs = qp.toString();
    return this.request(`/api/v1/hr/leave/upcoming${qs ? `?${qs}` : ''}`);
  }
  // Attendance (clock in/out, leave)
  async getAttendance(params = {}) {
    const qp = new URLSearchParams();
    if (params.date) qp.append('date', params.date);
    if (params.department_id) qp.append('department_id', params.department_id);
    const qs = qp.toString();
    return this.request(`/api/v1/hr/attendance${qs ? `?${qs}` : ''}`);
  }
  async getMyAttendance(startDate, endDate) {
    return this.request(`/api/v1/hr/attendance/me?start_date=${startDate}&end_date=${endDate}`);
  }
  async punchIn() {
    return this.request('/api/v1/hr/attendance/punch-in', { method: 'POST' });
  }
  async punchOut() {
    return this.request('/api/v1/hr/attendance/punch-out', { method: 'POST' });
  }
  async setAttendance(employeeId, targetDate, data) {
    return this.request(`/api/v1/hr/attendance/${employeeId}/date/${targetDate}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async getAnnouncements(departmentId = null) {
    const qs = departmentId ? `?department_id=${departmentId}` : '';
    return this.request(`/api/v1/hr/announcements${qs}`);
  }
  async createAnnouncement(data) {
    return this.request('/api/v1/hr/announcements', { method: 'POST', body: JSON.stringify(data) });
  }
  async getMyReviews() {
    return this.request('/api/v1/me/reviews');
  }
  async updateMyReview(reviewId, data) {
    return this.request(`/api/v1/me/reviews/${reviewId}`, { method: 'PATCH', body: JSON.stringify(data) });
  }
  async getMyDocuments() {
    return this.request('/api/v1/me/documents');
  }
  async createMyDocument(data) {
    return this.request('/api/v1/me/documents', { method: 'POST', body: JSON.stringify(data) });
  }
  async deleteMyDocument(linkId) {
    return this.request(`/api/v1/me/documents/${linkId}`, { method: 'DELETE' });
  }
  async getPTOPolicies(activeOnly = true) {
    return this.request(`/api/v1/hr/pto-policies?active_only=${activeOnly}`);
  }
  async createPTOPolicy(data) {
    return this.request('/api/v1/hr/pto-policies', { method: 'POST', body: JSON.stringify(data) });
  }
  async updatePTOPolicy(id, data) {
    return this.request(`/api/v1/hr/pto-policies/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async runPTOAccrual(year, month) {
    return this.request(`/api/v1/hr/pto/accrue?year=${year}&month=${month}`, { method: 'POST' });
  }
  async getOffboardingTasks(empId) {
    return this.request(`/api/v1/hr/people/${empId}/offboarding-tasks`);
  }
  async createOffboardingTask(empId, data) {
    return this.request(`/api/v1/hr/people/${empId}/offboarding-tasks`, { method: 'POST', body: JSON.stringify(data) });
  }
  async updateOffboardingTask(taskId, data) {
    return this.request(`/api/v1/hr/offboarding-tasks/${taskId}`, { method: 'PATCH', body: JSON.stringify(data) });
  }
  async getDocumentsForPerson(empId) {
    return this.request(`/api/v1/hr/people/${empId}/documents`);
  }
  async createDocumentForPerson(empId, data) {
    return this.request(`/api/v1/hr/people/${empId}/documents`, { method: 'POST', body: JSON.stringify(data) });
  }
  async deleteDocumentForPerson(empId, linkId) {
    return this.request(`/api/v1/hr/people/${empId}/documents/${linkId}`, { method: 'DELETE' });
  }

  // Compensation history
  async getCompensationHistory(empId) {
    return this.request(`/api/v1/hr/people/${empId}/compensation-history`);
  }
  async createCompensationHistory(empId, data) {
    return this.request(`/api/v1/hr/people/${empId}/compensation-history`, { method: 'POST', body: JSON.stringify(data) });
  }

  // Goals
  async getGoalsForPerson(empId) {
    return this.request(`/api/v1/hr/people/${empId}/goals`);
  }
  async createGoalForPerson(empId, data) {
    return this.request(`/api/v1/hr/people/${empId}/goals`, { method: 'POST', body: JSON.stringify(data) });
  }
  async updateGoal(goalId, data) {
    return this.request(`/api/v1/hr/goals/${goalId}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  // Company holidays
  async getHolidays(params = {}) {
    const qp = new URLSearchParams();
    if (params.start_date) qp.append('start_date', params.start_date);
    if (params.end_date) qp.append('end_date', params.end_date);
    const qs = qp.toString();
    return this.request(`/api/v1/hr/holidays${qs ? `?${qs}` : ''}`);
  }
  async createHoliday(data) {
    return this.request('/api/v1/hr/holidays', { method: 'POST', body: JSON.stringify(data) });
  }
  async updateHoliday(id, data) {
    return this.request(`/api/v1/hr/holidays/${id}`, { method: 'PUT', body: JSON.stringify(data) });
  }
  async deleteHoliday(id) {
    return this.request(`/api/v1/hr/holidays/${id}`, { method: 'DELETE' });
  }

  // Dependents
  async getDependentsForPerson(empId) {
    return this.request(`/api/v1/hr/people/${empId}/dependents`);
  }
  async createDependentForPerson(empId, data) {
    return this.request(`/api/v1/hr/people/${empId}/dependents`, { method: 'POST', body: JSON.stringify(data) });
  }
  async updateDependentForPerson(empId, dependentId, data) {
    return this.request(`/api/v1/hr/people/${empId}/dependents/${dependentId}`, { method: 'PATCH', body: JSON.stringify(data) });
  }
  async deleteDependentForPerson(empId, dependentId) {
    return this.request(`/api/v1/hr/people/${empId}/dependents/${dependentId}`, { method: 'DELETE' });
  }

  // PTO balances (per leave type)
  async getPTOBalancesForPerson(empId) {
    return this.request(`/api/v1/hr/people/${empId}/pto-balances`);
  }
  async upsertPTOBalance(empId, leaveType, data = null) {
    const qs = `?leave_type=${encodeURIComponent(leaveType)}`;
    return this.request(`/api/v1/hr/people/${empId}/pto-balances${qs}`, {
      method: 'PUT',
      body: JSON.stringify(data || {}),
    });
  }
  async updatePTOBalance(balanceId, data) {
    return this.request(`/api/v1/hr/pto-balances/${balanceId}`, { method: 'PATCH', body: JSON.stringify(data) });
  }

  // ── Applications ──────────────────────────────────────

  async submitApplication(formData) {
    const url = `${this.baseURL}/api/v1/applications`;
    const headers = {};
    if (this.token) headers['Authorization'] = `Bearer ${this.token}`;
    const response = await fetch(url, { method: 'POST', headers, body: formData });
    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `HTTP error! status: ${response.status}`);
    }
    return response.json();
  }

  async getMyApplications() {
    return this.request('/api/v1/applications/me');
  }

  async getApplication(id) {
    return this.request(`/api/v1/applications/${id}`);
  }

  async getAllApplications(params = {}) {
    const qp = new URLSearchParams();
    if (params.status) qp.append('status', params.status);
    if (params.page) qp.append('page', params.page);
    if (params.limit) qp.append('limit', params.limit);
    const qs = qp.toString();
    return this.request(`/api/v1/applications${qs ? `?${qs}` : ''}`);
  }

  async updateApplicationStatus(id, data) {
    return this.request(`/api/v1/applications/${id}/status`, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  async updateUserProfile(data) {
    return this.request('/api/auth/me', {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async getUsers(params = {}) {
    const qp = new URLSearchParams();
    if (params.page) qp.append('page', params.page);
    if (params.limit) qp.append('limit', params.limit);
    const qs = qp.toString();
    return this.request(`/api/auth/users${qs ? `?${qs}` : ''}`);
  }

  async updateUserRole(userId, role) {
    return this.request(`/api/auth/users/${userId}/role`, {
      method: 'PUT',
      body: JSON.stringify({ role }),
    });
  }

  // Self-service: goals, dependents
  async getMyGoals() {
    return this.request('/api/v1/me/goals');
  }
  async getMyDependents() {
    return this.request('/api/v1/me/dependents');
  }
  async createMyDependent(data) {
    return this.request('/api/v1/me/dependents', { method: 'POST', body: JSON.stringify(data) });
  }
  async updateMyDependent(dependentId, data) {
    return this.request(`/api/v1/me/dependents/${dependentId}`, { method: 'PATCH', body: JSON.stringify(data) });
  }
  async deleteMyDependent(dependentId) {
    return this.request(`/api/v1/me/dependents/${dependentId}`, { method: 'DELETE' });
  }
}

// Export singleton instance
const apiClient = new APIClient();
export default apiClient;
